// De dónde salen los productos que se ven (decisión D49): con sesión, del negocio con que se pide (sus precios); sin
// cuenta, del catálogo a la vista de la tienda (los precios de la lista de los clientes nuevos, o sin precios). Las
// pantallas del catálogo usan lo mismo para las dos cosas.
import type { DiaSemana } from '../compartido';
import { api } from './api';
import { useCuenta, useNegocio } from './cuenta';
import { useMarca } from './marca';
import { useSesion } from './sesion';
import { SLUG } from './tienda-actual';
import type { Articulo, Categoria, MarcaPublica, Pagina, Vitrina } from './tipos';

export type Fuente = { tipo: 'negocio'; id: string } | { tipo: 'publico' };

/** Para las cachés en memoria: lo del negocio no se mezcla con lo de la vista pública. */
export const claveFuente = (f: Fuente): string => (f.tipo === 'negocio' ? f.id : 'publico');

/** Se puede mirar el catálogo sin cuenta (con o sin precios). Sin el dato (API vieja): no. */
export const catalogoAbierto = (m: Pick<MarcaPublica, 'catalogo'> | null | undefined): boolean =>
  !!m?.catalogo && m.catalogo.publico !== 'cerrado';

/** La fuente de la pantalla: el negocio si hay sesión, la vista pública si la tienda la abre, o nada. */
export function useFuente(): Fuente | null {
  const sesion = useSesion((s) => !!s.sesion);
  const negocio = useNegocio();
  const abierto = useMarca((s) => catalogoAbierto(s.marca));
  if (sesion) return negocio ? { tipo: 'negocio', id: negocio.id } : null;
  return abierto ? { tipo: 'publico' } : null;
}

export interface FiltrosCatalogo {
  q?: string;
  categoria?: string | null;
  cursor?: string | null;
}

export const TAMANO_PAGINA = 40;

const consulta = (f: Fuente, extra: Record<string, string | number | null | undefined> = {}) =>
  f.tipo === 'negocio' ? { cliente_id: f.id, ...extra } : extra;
const ruta = (f: Fuente, recurso: string) =>
  f.tipo === 'negocio' ? `/tienda/${recurso}` : `/tienda/publico/${SLUG}/${recurso}`;
const sinSesion = (f: Fuente) => f.tipo === 'publico';

export const traerCatalogo = (f: Fuente, filtros: FiltrosCatalogo, signal?: AbortSignal) =>
  api<Pagina<Articulo>>(ruta(f, 'catalogo'), {
    consulta: consulta(f, {
      q: filtros.q?.trim(),
      categoria: filtros.categoria,
      cursor: filtros.cursor,
      limite: filtros.q?.trim() ? 60 : TAMANO_PAGINA,
    }),
    sinSesion: sinSesion(f),
    signal,
  });

export const traerCategorias = (f: Fuente) =>
  api<{ datos: Categoria[] }>(ruta(f, 'categorias'), { consulta: consulta(f), sinSesion: sinSesion(f) });

export const traerVitrina = (f: Fuente) =>
  api<Vitrina>(ruta(f, 'vitrina'), { consulta: consulta(f), sinSesion: sinSesion(f) });

export const traerArticulo = (f: Fuente, id: string) =>
  api<Articulo>(ruta(f, `articulos/${encodeURIComponent(id)}`), { consulta: consulta(f), sinSesion: sinSesion(f) });

/** Lo que las pantallas del catálogo necesitan saber de la tienda, con sesión o sin ella. */
export interface ReglasCatalogo {
  mostrarPrecio: boolean;
  permitirSinStock: boolean;
  fechas: string[];
  diasReparto?: DiaSemana[];
  zona: string;
  aviso: string;
}

export function useReglas(): ReglasCatalogo {
  const perfil = useCuenta((s) => s.perfil);
  const sesion = useSesion((s) => !!s.sesion);
  const marca = useMarca((s) => s.marca);
  if (sesion && perfil) {
    return {
      mostrarPrecio: perfil.tienda.mostrar_precio,
      permitirSinStock: perfil.tienda.permitir_sin_stock,
      fechas: perfil.tienda.fechas_entrega,
      zona: perfil.empresa.zona_horaria,
      aviso: perfil.tienda.aviso,
    };
  }
  const c = marca?.catalogo;
  return {
    mostrarPrecio: !!c && c.publico === 'con_precios' && c.mostrar_precio,
    permitirSinStock: c?.permitir_sin_stock ?? false,
    fechas: c?.fechas_entrega ?? [],
    zona: 'America/Asuncion',
    aviso: marca?.aviso ?? '',
  };
}
